<!--
  The whole record behind one line. Sections are horizontal runs of icon-and-value pairs naming
  themselves on hover — two dozen facts stacked one per line would waste the width beside them.
  Never draw an absent value: a column of dashes reads as data.
-->
<div class="flex flex-col gap-3">
  <!-- Which line this is. The panel opens beside a list of them, so it has to name its own. -->
  <section class="flex flex-col gap-1">
    <span class="flex items-start gap-2">
      <mat-icon
        class="mt-px h-4! w-4! shrink-0 text-base! leading-4! text-neutral-400"
        [matTooltip]="kindLabelKey() | translate"
        >{{ kindIcon() }}</mat-icon
      >
      <span class="min-w-0 flex-1 text-base font-semibold text-neutral-800">{{ prescription().name }}</span>
    </span>

    <div class="flex flex-wrap items-center gap-x-3 gap-y-1 pl-6 text-sm">
      <span class="shrink-0 text-neutral-500">{{ kindLabelKey() | translate }}</span>

      @if (prescription().code; as code) {
        <span
          class="inline-flex min-w-0 items-center gap-0.5 font-mono text-neutral-400"
          [matTooltip]="'guaranteeV2.label.serviceCode' | translate"
        >
          <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5!">tag</mat-icon>
          <span class="break-words">{{ code }}</span>
        </span>
      }

      <!-- A drug's amount, both numbers where both are set: packages are what the backend orders. -->
      @if (amount(); as prescribed) {
        @if (prescribed.required; as required) {
          <span
            class="shrink-0 tabular-nums"
            [class]="prescribed.overridden ? 'text-neutral-400' : 'font-semibold text-neutral-600'"
            [matTooltip]="(prescribed.overridden ? 'guaranteeV2.message.quantityOverriddenHint' : 'guaranteeV2.label.requiredQuantity') | translate"
            >&times;{{ required }}</span
          >
        }

        @if (prescribed.packages; as packageCount) {
          <span
            class="inline-flex shrink-0 items-center gap-1 rounded-full bg-info-50 px-1.5 py-0.5 font-semibold text-info-700 tabular-nums"
            [matTooltip]="'guaranteeV2.message.packagesHint' | translate: { count: packageCount }"
          >
            <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5!">inventory_2</mat-icon>
            {{ packageCount }}
          </span>
        }
      }

      <!-- Which of a course this is: members share a name, so the number is what tells them apart. -->
      @if (groupOrdinal(); as ordinal) {
        <span
          class="inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-neutral-100 font-semibold text-neutral-500 tabular-nums"
          [matTooltip]="'guaranteeV2.message.sessionOrdinalHint' | translate: { ordinal }"
          >{{ ordinal }}</span
        >
      }

      @if (isUnmapped()) {
        <guarantees-chip
          class="shrink-0"
          tone="warning"
          icon="link_off"
          [tooltip]="'guaranteeV2.message.unmappedHint' | translate"
          >{{ "guaranteeV2.label.unmapped" | translate }}</guarantees-chip
        >
      }

      @if (prescription().isFreeForm) {
        <guarantees-chip
          class="shrink-0"
          tone="warning"
          variant="outline"
          icon="edit_note"
          [tooltip]="'guaranteeV2.message.freeFormHint' | translate"
          >{{ "guaranteeV2.label.freeForm" | translate }}</guarantees-chip
        >
      }

      @if (prescription().medicalDataArrived) {
        <guarantees-chip
          class="shrink-0"
          tone="success"
          variant="outline"
          icon="lab_profile"
          [tooltip]="'guaranteeV2.message.medicalDataArrivedHint' | translate"
          >{{ "guaranteeV2.label.medicalData" | translate }}</guarantees-chip
        >
      }
    </div>
  </section>

  <!-- A deleted line must never read as a live one, so it says so before anything else about it. -->
  @if (prescription().isDeleted) {
    <guarantees-callout
      tone="error"
      stack
    >
      <span class="inline-flex flex-wrap items-center gap-1 text-sm font-semibold text-error-700">
        <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5!">delete</mat-icon>
        {{ "guaranteeV2.label.deleted" | translate }}
        @if (prescription().deletedAt; as deletedAt) {
          <span class="font-normal tabular-nums">{{ deletedAt | date: "dd.MM.yy HH:mm" }}</span>
        }
        @if (deletedByName(); as name) {
          <span class="font-normal">&middot; {{ name }}</span>
        }
      </span>
      @if (prescription().deletionReason; as reason) {
        <span class="text-sm text-error-700">{{ reason }}</span>
      }
    </guarantees-callout>
  }

  <!-- Ruling and engine verdict together: apart, they hide a grant against uncovered service. -->
  <guarantees-panel-section [label]="'guaranteeV2.label.decisionFacet' | translate">
    <div class="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
      <guarantees-chip
        class="shrink-0"
        [tone]="decisionTone()"
        [icon]="decisionIcon()"
        [tooltip]="decisionHintKey() | translate"
        >{{ decisionLabelKey() | translate }}</guarantees-chip
      >

      <guarantees-chip
        class="shrink-0"
        [tone]="coverageTone()"
        variant="outline"
        [icon]="coverageIcon()"
        [tooltip]="coverageHintKey() | translate"
        >{{ coverageLabelKey() | translate }}</guarantees-chip
      >

      @if (prescription().decision?.decidedAt; as decidedAt) {
        <span
          class="inline-flex min-w-0 items-center gap-1 text-neutral-500"
          [matTooltip]="authorityHintKey() | translate"
        >
          <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5! text-neutral-300">gavel</mat-icon>
          <span class="truncate">{{ decidedByName() || ("guaranteeV2.label.decidedAutomatically" | translate) }}</span>
          <span class="shrink-0 text-neutral-400 tabular-nums">{{ decidedAt | date: "dd.MM.yy HH:mm" }}</span>
        </span>
      }

      @if (prescription().coverageEvaluation?.evaluatedAt; as evaluatedAt) {
        <span
          class="inline-flex shrink-0 items-center gap-1 text-neutral-400 tabular-nums"
          [matTooltip]="'guaranteeV2.label.coverageEvaluatedAt' | translate"
        >
          <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5! text-neutral-300">policy</mat-icon>
          {{ evaluatedAt | date: "dd.MM.yy HH:mm" }}
        </span>
      }
    </div>

    <!-- What the line is adjudicated against. Without one the backend refuses a ruling, so its
         absence is reported rather than left as an empty row. -->
    <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
      @if (diagnosis(); as attached) {
        <guarantees-diagnosis-chip
          class="min-w-0"
          [diagnosis]="attached"
          [showCaseState]="false"
        ></guarantees-diagnosis-chip>
      } @else {
        <span class="inline-flex items-center gap-1 text-sm text-warning-800">
          <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5!">error_outline</mat-icon>
          {{ "guaranteeV2.message.lineWithoutDiagnosisHint" | translate }}
        </span>
      }
    </div>
  </guarantees-panel-section>

  <!-- Each note names the state it explains; a line can carry several at once. -->
  @if (notes().length > 0) {
    <section class="flex flex-col gap-1">
      @for (note of notes(); track note.key) {
        <guarantees-recorded-note [note]="note"></guarantees-recorded-note>
      }
    </section>
  }

  <!-- What it was prescribed for and what it costs — the terms the line is delivered under. -->
  <guarantees-panel-section [label]="'guaranteeV2.label.coverage' | translate">
    <div class="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
      @if (details()?.startDate || details()?.endDate) {
        <span
          class="inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-neutral-500 tabular-nums"
          [matTooltip]="'guaranteeV2.label.prescribedPeriod' | translate"
        >
          <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5! text-neutral-300">date_range</mat-icon>
          {{ details().startDate | date: "dd.MM.yy" }}
          @if (details().endDate) {
            <span>&ndash;&nbsp;{{ details().endDate | date: "dd.MM.yy" }}</span>
          }
        </span>
      }

      @if (financials()?.reserved; as reserved) {
        <span
          class="inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-neutral-500 tabular-nums"
          [matTooltip]="'guaranteeV2.message.reservedHint' | translate"
        >
          <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5! text-neutral-300">savings</mat-icon>
          {{ reserved | number: "1.0-2" }}&nbsp;&#8372;
        </span>
      }

      @if (financials()?.coverageFranchise !== undefined && financials()?.coverageFranchise !== null) {
        <span
          class="inline-flex shrink-0 items-center gap-0.5 text-neutral-500 tabular-nums"
          [matTooltip]="'guaranteeV2.label.coverageFranchise' | translate"
        >
          <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5! text-neutral-300">percent</mat-icon>
          {{ financials().coverageFranchise }}
        </span>
      }

      @if (financials()?.considerPaid) {
        <guarantees-chip
          class="shrink-0"
          tone="info"
          variant="outline"
          icon="paid"
          >{{ "guaranteeV2.label.considerPaid" | translate }}</guarantees-chip
        >
      }

      @if (prescription().completedInAppointment) {
        <guarantees-chip
          class="shrink-0"
          tone="success"
          variant="outline"
          icon="task_alt"
          [tooltip]="'guaranteeV2.message.completedInAppointmentHint' | translate"
          >{{ "guaranteeV2.label.completedInAppointment" | translate }}</guarantees-chip
        >
      }
    </div>

    <!-- The package options the line is guaranteed under, as copied onto it when it was ruled. -->
    @if (prescription().coverage; as coverage) {
      <div class="flex flex-col gap-0.5 text-sm">
        <span class="inline-flex items-center gap-1 text-neutral-500">
          <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5! text-neutral-300">inventory</mat-icon>
          <span [matTooltip]="'guaranteeV2.message.coverageSnapshotHint' | translate: { date: (coverage.snapshotAt | date: 'dd.MM.yy HH:mm') }">{{ coverage.name }}</span>
        </span>
        @for (option of coverage.options; track option.id) {
          <span class="pl-5 font-semibold text-neutral-700">{{ option.name }}</span>
          @if (option.limits?.length) {
            <guarantees-package-limits
              class="pl-5"
              [limits]="option.limits"
            ></guarantees-package-limits>
          }
          @for (sub of option.subOptions ?? []; track sub.id) {
            <span class="pl-8 text-neutral-500">— {{ sub.name }}</span>
            @if (sub.limits?.length) {
              <guarantees-package-limits
                class="pl-8"
                [limits]="sub.limits"
              ></guarantees-package-limits>
            }
          }
        }
      </div>
    }

    <!-- The clinical conditions the code carries, and how the line was classified for processing. -->
    @if (conditions().length > 0 || categories().length > 0) {
      <div class="flex flex-wrap gap-1">
        @for (condition of conditions(); track condition.id) {
          <guarantees-chip
            tone="neutral"
            variant="outline"
            [tooltip]="condition.type + ': ' + condition.value"
            >{{ condition.description }}</guarantees-chip
          >
        }
        @for (category of categories(); track category) {
          <guarantees-chip
            tone="neutral"
            icon="label"
            >{{ category }}</guarantees-chip
          >
        }
      </div>
    }
  </guarantees-panel-section>

  <!-- Medication specifics: what to take, how much, for how long, and how much to buy. -->
  @if (drugDetails(); as drug) {
    <section class="flex flex-col gap-1.5 rounded-lg bg-primary-50 px-2 py-2">
      <!-- How it is dispensed. How much of it to buy is stated with the line's name, above. -->
      @if (drug.form || drug.protocolIdentifier) {
        <div class="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-neutral-500">
          @if (drug.form; as form) {
            <span
              class="inline-flex shrink-0 items-center gap-1"
              [matTooltip]="'guaranteeV2.label.form' | translate"
            >
              <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5! text-neutral-400">science</mat-icon>
              {{ form }}
            </span>
          }
          @if (drug.protocolIdentifier; as protocol) {
            <span
              class="inline-flex min-w-0 items-center gap-1 font-mono text-neutral-400"
              [matTooltip]="'guaranteeV2.label.protocolIdentifier' | translate"
            >
              <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5!">description</mat-icon>
              <span class="truncate">{{ protocol }}</span>
            </span>
          }
        </div>
      }

      <!-- The whole schedule as one run: dose, how often, for how long, and against meals. -->
      @if (schedule(); as dose) {
        <div class="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-neutral-600">
          @if (dose.dose) {
            <span
              class="inline-flex shrink-0 items-center gap-1 whitespace-nowrap tabular-nums"
              [matTooltip]="'guaranteeV2.label.dose' | translate"
            >
              <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5! text-neutral-400">medication</mat-icon>
              {{ dose.dose }} {{ dose.doseUnit }}
            </span>
          }
          @if (dose.frequency) {
            <span
              class="inline-flex shrink-0 items-center gap-1 whitespace-nowrap tabular-nums"
              [matTooltip]="'guaranteeV2.label.frequency' | translate"
            >
              <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5! text-neutral-400">repeat</mat-icon>
              {{ dose.frequency }}× / {{ dose.period }} {{ timeUnitKey(dose.periodUnit) | translate }}
            </span>
          }
          @if (dose.duration) {
            <span
              class="inline-flex shrink-0 items-center gap-1 whitespace-nowrap tabular-nums"
              [matTooltip]="'guaranteeV2.label.duration' | translate"
            >
              <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5! text-neutral-400">schedule</mat-icon>
              {{ dose.duration }} {{ timeUnitKey(dose.durationUnit) | translate }}
            </span>
          }
          <!-- What the schedule adds up to, kept apart from what it is made of by their own icons. -->
          @if (dose.dailyDose) {
            <span
              class="inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-neutral-500 tabular-nums"
              [matTooltip]="'guaranteeV2.label.dailyDose' | translate"
            >
              <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5! text-neutral-400">today</mat-icon>
              {{ dose.dailyDose }} {{ dose.doseUnit }}
            </span>
          }
          @if (dose.courseDose) {
            <span
              class="inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-neutral-500 tabular-nums"
              [matTooltip]="'guaranteeV2.label.courseDose' | translate"
            >
              <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5! text-neutral-400">functions</mat-icon>
              {{ dose.courseDose }} {{ dose.doseUnit }}
            </span>
          }
          @if (dose.mealRelation) {
            <span
              class="inline-flex shrink-0 items-center gap-1 whitespace-nowrap"
              [matTooltip]="'guaranteeV2.label.mealRelation' | translate"
            >
              <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5! text-neutral-400">restaurant</mat-icon>
              {{ scheduleMealKey() | translate }}
            </span>
          }
          @if (dose.timesOfDay?.length) {
            <span
              class="inline-flex shrink-0 items-center gap-1 whitespace-nowrap tabular-nums"
              [matTooltip]="'guaranteeV2.label.timesOfDay' | translate"
            >
              <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5! text-neutral-400">alarm</mat-icon>
              {{ dose.timesOfDay.join(", ") }}
            </span>
          }
          @if (dose.asNeeded) {
            <guarantees-chip
              class="shrink-0"
              tone="neutral"
              variant="outline"
              icon="pending_actions"
              >{{ "guaranteeV2.label.asNeeded" | translate }}</guarantees-chip
            >
          }
        </div>
      }

      <!-- One truncated line each: a common substance matches a dozen. Form is stated once above. -->
      @if (drugs().length > 0) {
        <div class="flex flex-col gap-0.5">
          <span [class]="sectionLabel"> {{ "guaranteeV2.label.trademarks" | translate }} &middot; {{ drugs().length }} </span>

          <!-- By position: a drug carries no guaranteed identity, and two uncoded ones collide. -->
          @for (item of visibleDrugs(); track $index) {
            <span class="flex items-baseline gap-2 text-sm">
              <span
                class="min-w-0 flex-1 truncate text-neutral-700"
                [matTooltip]="item.trademarkFullName || item.nameUa"
                >{{ item.trademarkFullName || item.nameUa }}</span
              >
              @if (item.quantityInPackage) {
                <span
                  class="shrink-0 text-neutral-400 tabular-nums"
                  [matTooltip]="'guaranteeV2.label.quantityInPackage' | translate"
                  >{{ item.quantityInPackage | number: "1.0-2" }}</span
                >
              }
              <!-- How a course converts into whole packs, which is what a pharmacy can actually sell. -->
              @if (item.packagingFactor) {
                <span
                  class="shrink-0 text-neutral-400 tabular-nums"
                  [matTooltip]="'guaranteeV2.label.packagingFactor' | translate"
                  >&times;{{ item.packagingFactor }}</span
                >
              }
              @if (item.atcCode) {
                <span
                  class="shrink-0 font-mono text-neutral-400"
                  [matTooltip]="'guaranteeV2.label.atcCode' | translate"
                  >{{ item.atcCode }}</span
                >
              }
              @if (item.code) {
                <span
                  class="shrink-0 font-mono text-neutral-300"
                  [matTooltip]="'guaranteeV2.label.serviceCode' | translate"
                  >{{ item.code }}</span
                >
              }
              @if (item.requiresRecipe) {
                <mat-icon
                  class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5! text-info-600"
                  [matTooltip]="'guaranteeV2.message.requiresRecipeHint' | translate"
                  >receipt</mat-icon
                >
              }
              @if (item.isRecommended) {
                <mat-icon
                  class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5! text-success-600"
                  [matTooltip]="'guaranteeV2.message.recommendedDrugHint' | translate"
                  >verified</mat-icon
                >
              }
            </span>
          }

          @if (hiddenDrugCount(); as hidden) {
            <button
              class="inline-flex items-center gap-1 self-start rounded text-sm font-semibold text-primary-600 hover:underline focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none"
              type="button"
              (click)="toggleTrademarks()"
            >
              <mat-icon class="h-3.5! w-3.5! text-sm! leading-3.5!">expand_more</mat-icon>
              {{ "guaranteeV2.action.showAllTrademarks" | translate: { count: hidden } }}
            </button>
          } @else if (isTrademarkListFolded()) {
            <button
              class="inline-flex items-center gap-1 self-start rounded text-sm font-semibold text-neutral-500 hover:underline focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none"
              type="button"
              (click)="toggleTrademarks()"
            >
              <mat-icon class="h-3.5! w-3.5! text-sm! leading-3.5!">expand_less</mat-icon>
              {{ "guaranteeV2.action.collapseAll" | translate }}
            </button>
          }
        </div>
      }

      @if (drug.comment) {
        <p class="text-sm text-neutral-500">{{ drug.comment }}</p>
      }
    </section>
  }

  <!-- Newest first — a cancelled slot is as much an answer as a kept one — in the line's own shape. -->
  @if (planningRows().length > 0) {
    <guarantees-panel-section [label]="'guaranteeV2.label.planning' | translate">
      <div class="flex flex-col gap-1">
        @for (row of planningRows(); track row.planning.id) {
          <div class="flex flex-col gap-1 rounded-lg border border-neutral-200 px-2 py-1.5">
            <div class="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
              <!-- The attempt's own state is the only labelled status here; the rest name themselves. -->
              <guarantees-chip
                class="shrink-0"
                [tone]="attemptTone(row.planning)"
                variant="outline"
                [icon]="attemptIcon(row.planning)"
                >{{ attemptLabelKey(row.planning) | translate }}</guarantees-chip
              >

              @if (row.planning.syncState && row.planning.syncState !== "None") {
                <guarantees-chip
                  class="shrink-0"
                  tone="info"
                  variant="outline"
                  [icon]="syncIcon(row.planning.syncState)"
                  iconOnly
                  [tooltip]="
                    'guaranteeV2.message.pendingSyncHint'
                      | translate: { operation: syncLabelKey(row.planning.syncState) | translate, state: attemptLabelKey(row.planning) | translate }
                  "
                  >{{ syncLabelKey(row.planning.syncState) | translate }}</guarantees-chip
                >
              }

              @if (row.planning.dateStart; as dateStart) {
                <span
                  class="shrink-0 font-semibold whitespace-nowrap text-neutral-700 tabular-nums"
                  [matTooltip]="'guaranteeV2.label.plannedDate' | translate"
                >
                  {{ dateStart | date: "dd.MM.yy HH:mm" }}
                  @if (row.planning.dateEnd) {
                    <span class="font-normal text-neutral-400">&ndash; {{ row.planning.dateEnd | date: "HH:mm" }}</span>
                  }
                </span>
              }

              @if (row.doctorName; as doctorName) {
                <span
                  class="inline-flex min-w-0 items-center gap-1 text-neutral-600"
                  [matTooltip]="'guaranteeV2.label.doctor' | translate"
                >
                  <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5! text-neutral-300">stethoscope</mat-icon>
                  <span class="truncate">{{ doctorName }}</span>
                </span>
              }

              <span class="ml-auto flex shrink-0 items-center gap-1.5">
                @if (row.targetGuaranteeId; as targetGuaranteeId) {
                  <button
                    class="inline-flex shrink-0 items-center gap-0.5 rounded px-1 text-sm font-semibold text-primary-600 hover:bg-primary-50 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none"
                    type="button"
                    [matTooltip]="'guaranteeV2.message.openBookedVisitHint' | translate"
                    (click)="guaranteeOpened.emit(targetGuaranteeId)"
                  >
                    <mat-icon class="h-3.5! w-3.5! text-sm! leading-3.5!">arrow_forward</mat-icon>
                    {{ "guaranteeV2.action.openVisit" | translate }}
                  </button>
                }
                @if (row.meetingLink; as meetingLink) {
                  <a
                    class="inline-flex items-center gap-0.5 rounded px-1 text-sm font-semibold text-primary-600 hover:underline"
                    target="_blank"
                    rel="noopener"
                    [href]="meetingLink"
                    [matTooltip]="'guaranteeV2.label.meetingLink' | translate"
                  >
                    <mat-icon class="h-3.5! w-3.5! text-sm! leading-3.5!">videocam</mat-icon>
                    {{ "guaranteeV2.action.joinMeeting" | translate }}
                  </a>
                }
              </span>
            </div>

            <!-- Where it was to happen, what it was to cost, and what identifies it at the partner. -->
            <div class="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-neutral-500">
              @if (row.planning.partnerName; as partnerName) {
                <span
                  class="inline-flex max-w-56 items-center gap-1"
                  [matTooltip]="'guaranteeV2.label.partner' | translate"
                >
                  <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5! text-neutral-300">business</mat-icon>
                  <span class="truncate">{{ partnerName }}</span>
                </span>
              }

              @if (row.planning.locationAddress; as address) {
                <span
                  class="inline-flex max-w-72 items-center gap-1 text-neutral-400"
                  [matTooltip]="'guaranteeV2.label.location' | translate"
                >
                  <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5! text-neutral-300">location_on</mat-icon>
                  <span class="truncate">{{ address }}</span>
                </span>
              }

              @if (row.planning.price; as price) {
                <span
                  class="inline-flex shrink-0 items-center gap-1 whitespace-nowrap tabular-nums"
                  [matTooltip]="'guaranteeV2.label.price' | translate"
                >
                  <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5! text-neutral-300">payments</mat-icon>
                  {{ price | number: "1.0-2" }}&nbsp;&#8372;
                </span>
              }

              @if (row.planning.effectiveFranchise !== undefined && row.planning.effectiveFranchise !== null) {
                <span
                  class="inline-flex shrink-0 items-center gap-0.5 tabular-nums"
                  [matTooltip]="'guaranteeV2.label.effectiveFranchise' | translate"
                >
                  <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5! text-neutral-300">percent</mat-icon>
                  {{ row.planning.effectiveFranchise }}
                </span>
              }

              @if (row.orderNumber || row.prescriptionNumber) {
                <span
                  class="inline-flex min-w-0 items-center gap-1 font-mono text-neutral-400"
                  [matTooltip]="(row.orderNumber ? 'guaranteeV2.label.orderNumber' : 'guaranteeV2.label.prescriptionNumber') | translate"
                >
                  <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5! text-neutral-300">receipt_long</mat-icon>
                  <span class="truncate">{{ row.orderNumber || row.prescriptionNumber }}</span>
                </span>
              }

              <!-- When the partner last moved it, which is what a stale booking is spotted by. -->
              @if (row.planning.lastUpdateDate; as lastUpdate) {
                <span
                  class="inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-neutral-400 tabular-nums"
                  [matTooltip]="'guaranteeV2.label.lastUpdate' | translate"
                >
                  <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5! text-neutral-300">sync</mat-icon>
                  {{ lastUpdate | date: "dd.MM.yy HH:mm" }}
                </span>
              }
            </div>
          </div>
        }
      </div>
    </guarantees-panel-section>
  }

  <guarantees-record-breaches [breaches]="breaches()"></guarantees-record-breaches>

  <!-- Named, and last change first: two bare timestamps side by side cannot be told apart. -->
  <div class="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-neutral-100 pt-2 text-xs leading-4 text-neutral-500">
    <span
      class="inline-flex shrink-0 items-center gap-1"
      [matTooltip]="creatorHintKey() | translate"
    >
      <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5! text-neutral-300">{{ creatorIcon() }}</mat-icon>
      <span class="font-medium tracking-wide text-neutral-300 uppercase">{{ "guaranteeV2.label.createdAt" | translate }}</span>
      <span class="tabular-nums">{{ prescription().createdAt | date: "dd.MM.yy HH:mm" }}</span>
      @if (createdByName() | guaranteesActorName; as name) {
        <span>&middot; {{ name }}</span>
      }
    </span>

    @if (wasUpdated()) {
      <span class="inline-flex shrink-0 items-center gap-1">
        <span class="font-medium tracking-wide text-neutral-300 uppercase">{{ "guaranteeV2.label.updatedAt" | translate }}</span>
        <span class="tabular-nums">{{ prescription().updatedAt | date: "dd.MM.yy HH:mm" }}</span>
        @if (updatedByName() | guaranteesActorName; as name) {
          <span>&middot; {{ name }}</span>
        }
      </span>
    }
  </div>

  <!-- Reading the record's past belongs with the record, not among the acts that change it. -->
  <guarantees-change-history
    kind="prescription"
    [recordId]="prescription().id"
  ></guarantees-change-history>
</div>
